/**
 * One project's dashboard (Lj, Nov 1: "see where im at, while im working"). The project type and subtype pick the work
 * cards, the contract type picks the money card; a card with no data comes back null and doesn't show. Costing, burn
 * and scope come from the code the rest of the app already uses, so a card and the job cost screen never disagree.
 * Pure — no I/O.
 */
import { jobBurn, isMaterialRun } from './deptgoals.js';
import { labourInPrice, projectEstMinutes } from './jobpay.js';
import { contractValueOf } from './profit.js';
import { extrasOf, planStartFor } from './scope.js';
import { addDaysStr } from './phonestats.js';
import { localDateStr } from './time.js';
import { TRIP_DEFAULTS } from './trips.js';
import { isFuelLine } from './metrics.js';

const PAUSE_LABEL = { pause: 'Pause', break: 'Break', waiting: 'Waiting', blocked: 'Blocked' };
const costKind = (l) => (l?.category === 'fuel' || isFuelLine(l) ? 'fuel' : l?.category === 'materials' ? 'materials' : 'other');

export const CONTRACT_TYPES = Object.freeze(['lump_sum', 'hourly', 'cost_plus', 'not_to_exceed']);
export const CONTRACT_LABEL = Object.freeze({ lump_sum: 'Lump sum', hourly: 'Hourly', cost_plus: 'Cost plus', not_to_exceed: 'Not to exceed' });
const FROM_BOOKS = { fixed_price: 'lump_sum', time_and_materials: 'hourly', cost_plus: 'cost_plus', not_to_exceed: 'not_to_exceed' };
const FROM_JOB_TYPE = { fixed: 'lump_sum', product: 'lump_sum', hourly: 'hourly' };

const DAY_MIN = 480;   // a work day, for finish dates and ahead / behind
const H = 3600000;
const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const open = (t) => t && t.status !== 'done' && t.status !== 'skipped' && t.status !== 'void' && !t.deletedAt;
const isWeekday = (d) => { const w = new Date(`${d}T12:00:00`).getDay(); return w !== 0 && w !== 6; };
const round2 = (v) => Math.round(v * 100) / 100;

/** How the job pays: the project's pick, else the books job's costing type, else its job type. Nothing to migrate. null = no contract. */
export function contractTypeOf(project, job = null) {
  if (CONTRACT_TYPES.includes(project?.contractType)) return project.contractType;
  return FROM_BOOKS[job?.costingType] || FROM_JOB_TYPE[project?.jobType] || null;
}

/** Quoted minutes finished vs quoted minutes dated up to today, in 8 h days (to the half). null without dated lines. */
export function aheadDaysOf(lines, today) {
  if (!lines.some((t) => t.scheduledDate)) return null;
  const planned = lines.filter((t) => t.scheduledDate && t.scheduledDate <= today).reduce((a, t) => a + num(t.estimatedMinutes), 0);
  const finished = lines.filter((t) => t.status === 'done').reduce((a, t) => a + num(t.estimatedMinutes), 0);
  return Math.round(((finished - planned) / DAY_MIN) * 2) / 2;
}

/** The day `minutes` of work finishes, weekdays at 8 h, starting `from`. */
export function finishDate(minutes, from) {
  let left = minutes; let d = from;
  for (let i = 0; i < 800; i += 1) {
    if (isWeekday(d)) { left -= DAY_MIN; if (left <= 0) return d; }
    d = addDaysStr(d, 1);
  }
  return d;
}

const countWeekdays = (from, to) => { let n = 0; for (let d = from, i = 0; d <= to && i < 800; d = addDaysStr(d, 1), i += 1) if (isWeekday(d)) n += 1; return n; };

/** The plan as a line: quoted hours by day from the first dated line to the last, at most a work day per weekday (a 36 h line dated one day runs on). */
export function planCurve(lines) {
  const dated = lines.filter((t) => t.scheduledDate).sort((a, b) => String(a.scheduledDate).localeCompare(String(b.scheduledDate)));
  if (!dated.length) return null;
  const out = []; let cum = 0; let carry = 0; let d = dated[0].scheduledDate; let i = 0;
  while ((i < dated.length || carry > 0) && out.length < 400) {
    while (i < dated.length && dated[i].scheduledDate <= d) { carry += num(dated[i].estimatedMinutes); i += 1; }
    if (isWeekday(d) || i >= dated.length) { const take = isWeekday(d) ? Math.min(carry, DAY_MIN) : 0; cum += take; carry -= take; }
    out.push({ date: d, minutes: cum });
    d = addDaysStr(d, 1);
  }
  return out;
}

/** One task in a card's detail: what was quoted, what it took, whether it's done. */
const taskRow = (t) => ({ id: t.id, title: t.title || 'Task', done: t.status === 'done', date: t.scheduledDate || null, estMin: num(t.estimatedMinutes) || null, spentMs: num(t.timeSpent) });

/** Phases: the project's sections with quoted hours in them, else the type's tag map (`dashboard.phaseMap`: [{ name, tags }]). */
function phasesOf(lines, sections, type) {
  const row = (name, list) => ({ name, estMin: list.reduce((a, t) => a + num(t.estimatedMinutes), 0), doneMin: list.filter((t) => t.status === 'done').reduce((a, t) => a + num(t.estimatedMinutes), 0), open: list.filter(open).length, doneN: list.filter((t) => t.status === 'done').length, totalN: list.length, spentMs: list.reduce((a, t) => a + num(t.timeSpent), 0), tasks: list.map(taskRow) });
  const bySection = sections.map((s) => row(s.title || 'Phase', lines.filter((t) => t.sectionId === s.id))).filter((p) => p.estMin > 0);
  if (bySection.length) return bySection;
  const map = type?.dashboard?.phaseMap;
  if (!Array.isArray(map) || !map.length) return null;
  // `first` claims its lines before the others (a deck roof's demo belongs to the deck, not the tear-off); the order shown stays the map's.
  const seen = new Set();
  const claim = (p) => {
    const tags = (p.tags || []).map((x) => String(x).toLowerCase());
    const list = lines.filter((t) => !seen.has(t.id) && (t.tags || []).some((x) => tags.includes(String(x).toLowerCase())));
    list.forEach((t) => seen.add(t.id));
    return list;
  };
  const claimed = new Map();
  for (const p of map.filter((x) => x.first)) claimed.set(p, claim(p));
  for (const p of map.filter((x) => !x.first)) claimed.set(p, claim(p));
  const rows = map.map((p) => row(p.name, claimed.get(p))).filter((p) => p.estMin > 0);
  return rows.length ? rows : null;
}

/** Units of the work (`dashboard.unit`: { label, prefix, steps: [{ name, tag }], names }): roof planes, rooms. Tasks tagged prefix + n. */
function unitsOf(mine, type) {
  const u = type?.dashboard?.unit;
  if (!u?.prefix) return null;
  const by = new Map();
  for (const t of mine) for (const tag of t.tags || []) {
    const s = String(tag).toLowerCase();
    if (s.startsWith(u.prefix)) { if (!by.has(s)) by.set(s, []); by.get(s).push(t); }
  }
  if (!by.size) return null;
  const rows = [...by.entries()].sort((a, b) => a[0].localeCompare(b[0], undefined, { numeric: true })).map(([tag, list]) => {
    const steps = (u.steps || []).map((st) => { const on = list.filter((t) => (t.tags || []).some((x) => String(x).toLowerCase() === st.tag)); return { name: st.name, done: on.length > 0 && on.every((t) => t.status === 'done'), any: on.length > 0 }; });
    const title = (list[0]?.title || '').split(' — ')[1]?.split(' (')[0] || '';
    return {
      id: tag, label: `${u.short || u.label?.[0] || ''}${tag.slice(u.prefix.length)}`, title,
      tasks: list.map(taskRow), done: list.every((t) => t.status === 'done'), estMin: list.reduce((a, t) => a + num(t.estimatedMinutes), 0), spentMs: list.reduce((a, t) => a + num(t.timeSpent), 0), steps,
    };
  });
  return { label: u.label || 'Units', rows, done: rows.filter((r) => r.done).length, open: u.sameDay ? rows.filter((r) => !r.done && r.steps.some((s) => s.done)).map((r) => r.label) : [] };
}

/**
 * → { contract, where, watch, next, phases, units, plan, time, costs, extras, money }.
 * `data` = { tasks, sessions, sections, job, cost (computeProjectCosting), receipts, vehicle, type, pay, roles, now }.
 */
export function projectDashboard(project, data = {}) {
  const now = data.now || Date.now();
  const today = localDateStr(now);
  const tasks = data.tasks || [];
  const job = data.job || null;
  const cost = data.cost || null;
  const type = data.type || null;
  const mine = tasks.filter((t) => t && t.projectId === project.id && !t.deletedAt && !t.bucket && t.status !== 'void');
  const top = mine.filter((t) => !t.parentId && !t.quickTaskId && t.status !== 'skipped');
  const parents = new Set(mine.filter((t) => t.parentId && num(t.estimatedMinutes) > 0).map((t) => t.parentId));
  const lines = mine.filter((t) => num(t.estimatedMinutes) > 0 && !parents.has(t.id));
  const contract = contractTypeOf(project, job);
  const burn = jobBurn(tasks.filter((t) => t.status !== 'void'), project.id);

  // ── where I'm at ──────────────────────────────────────────────────────────────────────────────────────────────
  const quoted = burn?.estMin > 0;
  const leftMin = quoted ? Math.round(burn.estMin * (1 - burn.doneShare)) : 0;
  const doneN = top.filter((t) => t.status === 'done').length;
  const due = project.endDate || null;
  const daysLeft = due && due >= today ? countWeekdays(today, due) : null;
  const where = {
    pct: quoted ? Math.round(burn.doneShare * 100) : top.length ? Math.round((doneN / top.length) * 100) : null,
    by: quoted ? 'estimate' : 'tasks', doneN, totalN: top.length, estMin: quoted ? burn.estMin : 0, leftMin,
    due, daysLeft, spareMin: daysLeft != null ? daysLeft * DAY_MIN - leftMin : null,
    finish: quoted && leftMin > 0 && due ? finishDate(leftMin, today) : null,
    aheadDays: due ? aheadDaysOf(lines, today) : null,
  };

  // ── next up: overdue and today first, in order ────────────────────────────────────────────────────────────────
  const order = (t) => num(t.sortOrder) || num(t.manualOrder) || num(t.createdAt);
  const openTop = top.filter(open);
  const dated = openTop.filter((t) => t.scheduledDate).sort((a, b) => String(a.scheduledDate).localeCompare(String(b.scheduledDate)) || order(a) - order(b));
  const undated = openTop.filter((t) => !t.scheduledDate).sort((a, b) => order(a) - order(b));
  const allOpen = [...dated, ...undated].map(taskRow);
  const next = [...dated, ...undated].slice(0, 3).map((t) => ({ task: t, late: !!t.scheduledDate && t.scheduledDate < today, lateDays: t.scheduledDate && t.scheduledDate < today ? Math.round((new Date(`${today}T12:00:00`) - new Date(`${t.scheduledDate}T12:00:00`)) / 86400000) : 0, estMin: num(t.estimatedMinutes) || null }));

  // ── the work ──────────────────────────────────────────────────────────────────────────────────────────────────
  const sections = (data.sections || []).filter((s) => s.projectId === project.id);
  const phases = phasesOf(lines, sections, type);
  const units = unitsOf(mine, type);
  const curve = due ? planCurve(lines) : null;
  const doneMin = lines.filter((t) => t.status === 'done').reduce((a, t) => a + num(t.estimatedMinutes), 0);
  const plan = curve ? { curve, doneMin, today, estMin: curve[curve.length - 1].minutes } : null;

  // ── time: each kind its own card, with what's behind it. Every task lands in one of the four, so they add up to on tasks. ──
  const worked = mine.filter((t) => num(t.timeSpent) > 0).sort((x, y) => num(y.timeSpent) - num(x.timeSpent));
  const lineIds = new Set(lines.map((t) => t.id));
  const isOver = (t) => t.scope === 'overhead';
  const bucket = (list) => ({ ms: list.reduce((a, t) => a + num(t.timeSpent), 0), tasks: list.map(taskRow) });
  const quotedTime = bucket(worked.filter((t) => !isOver(t) && lineIds.has(t.id)));
  const overheadTime = bucket(worked.filter(isOver));
  const pickupTime = bucket(worked.filter((t) => !isOver(t) && !lineIds.has(t.id) && isMaterialRun(t)));
  const otherTime = bucket(worked.filter((t) => !isOver(t) && !lineIds.has(t.id) && !isMaterialRun(t)));
  const onTask = worked.reduce((a, t) => a + num(t.timeSpent), 0);

  // every break: the task's status log; what the task totals hold beyond it (older breaks) is shown as one line
  const taskIds = new Set(mine.map((t) => t.id));
  const breakRows = mine.flatMap((t) => (t.statusLog || []).filter((l) => l?.startedAt && num(l.duration) > 0).map((l) => ({ at: l.startedAt, date: localDateStr(l.startedAt), ms: num(l.duration), taskId: t.id, title: t.title || 'Task', kind: PAUSE_LABEL[l.type] || 'Pause', reason: String(l.context || '').trim() || null })))
    .sort((x, y) => y.at - x.at);
  const breakListed = breakRows.reduce((a, r) => a + r.ms, 0);
  const breakTotal = Math.max(num(cost?.hours?.breakMs), breakListed);
  const groupBreaks = (keyOf) => {
    const by = new Map();
    for (const r of breakRows) { const k = keyOf(r); const g = by.get(k) || { key: k, ms: 0, n: 0, taskId: r.taskId, title: r.title }; g.ms += r.ms; g.n += 1; by.set(k, g); }
    return [...by.values()];
  };
  const clockedMs = num(cost?.hours?.clockedMs);

  // every drive on this job: the same attribution as job costing (a drive on the project, or its stretches on this job's tasks)
  const driveRows = [];
  for (const d of data.sessions || []) {
    if (d?.type !== 'travel' || !d.startedAt || (!d.endedAt && localDateStr(d.startedAt) !== today)) continue;
    const dur = num(d.duration) || (d.endedAt ? d.endedAt - d.startedAt : now - d.startedAt);
    const segs = (Array.isArray(d.taskSegments) ? d.taskSegments : []).filter((g) => g.projectId === project.id || taskIds.has(g.taskId));
    const ms = d.projectId === project.id ? dur : segs.reduce((a, g) => a + Math.max(0, (g.endedAt || d.endedAt || now) - g.startedAt), 0);
    if (!(ms > 0)) continue;
    driveRows.push({ id: d.id, at: d.startedAt, endedAt: d.endedAt || null, date: localDateStr(d.startedAt), ms, km: round2((num(d.distanceM) / 1000) * (dur > 0 ? Math.min(1, ms / dur) : 1)) });
  }
  driveRows.sort((x, y) => y.at - x.at);
  const driveKm = round2(driveRows.reduce((a, r) => a + r.km, 0));
  const shadowRow = (cost?.cfo?.labour?.byPerson || []).find((p) => num(p.paidOut) > 0) || null;
  const time = onTask > 0 || driveRows.length ? {
    onTaskMs: onTask, days: num(cost?.hours?.days), quoted: quotedTime, other: otherTime, overhead: overheadTime, pickups: pickupTime,
    breaks: {
      ms: breakTotal, n: breakRows.length, rows: breakRows, unlistedMs: Math.max(0, breakTotal - breakListed),
      avgMs: breakRows.length ? Math.round(breakListed / breakRows.length) : 0, longestMs: breakRows.reduce((a, r) => Math.max(a, r.ms), 0),
      pctOfClock: clockedMs > 0 ? Math.round((breakTotal / clockedMs) * 100) : null,
      byKind: groupBreaks((r) => r.kind).sort((x, y) => y.ms - x.ms),
      byReason: groupBreaks((r) => r.reason || 'No reason given').sort((x, y) => y.ms - x.ms),
      byDay: groupBreaks((r) => r.date).sort((x, y) => (x.key < y.key ? 1 : -1)),
      byTask: groupBreaks((r) => r.taskId).sort((x, y) => y.ms - x.ms),
    },
    travel: { ms: driveRows.reduce((a, r) => a + r.ms, 0), km: driveKm, n: driveRows.length, rows: driveRows },
    yourTime: num(cost?.cfo?.labour?.paidOut) > 0 ? { amount: Math.round(num(cost.cfo.labour.paidOut)), hours: Math.round(num(shadowRow?.hours) * 10) / 10, rate: Math.round(num(shadowRow?.rates?.paidOut)) } : null,
  } : null;

  // ── job costs: posted in the books against the bid's budget; fuel on its own ──────────────────────────────────
  const budget = job?.budget || null;
  const actual = job?.actual || null;
  const bid = project.bidBudget || null;   // the linked bid's cost budgets; else the books job's
  const rows = (bid ? [['materials', 'Materials', bid.materials], ['equipment', 'Equipment', bid.equipment], ['other', 'Dump + other', bid.other]] : [['materials', 'Materials', num(budget?.materials)], ['subcontractor', 'Subs', num(budget?.subcontractor)], ['other', 'Other', num(budget?.other)]])
    .map(([k, label, b]) => ({ key: k, label, budget: num(b), posted: k === 'equipment' ? 0 : num(actual?.[k]) })).filter((r) => r.budget > 0 || r.posted > 0);
  const vehicle = { ...TRIP_DEFAULTS, ...(data.vehicle || {}) };
  const fuelReceipts = (data.receipts || []).flatMap((r) => Object.values(r?.pages || {}).filter((p) => p && p.status !== 'void').flatMap((p) => p.lines || [])).filter((l) => l.projectId === project.id && isFuelLine(l)).reduce((a, l) => a + num(l.amount), 0);
  const km = time?.travel.km || num(cost?.hours?.travelKm);
  // every transaction we can name on this job: the books' feed (split receipts by kind), money logged here, receipts snapped on the job
  const feed = Array.isArray(job?.expenses) ? job.expenses : [];
  const seen = new Set(feed.map((e) => e.id));
  const tx = [];
  const pushLines = (base, lines) => {
    const by = new Map();
    for (const l of lines) { const k = costKind(l); const g = by.get(k) || { amount: 0, what: null }; g.amount += num(l.amount); g.what = g.what || l.description || null; by.set(k, g); }
    for (const [kind, g] of by) tx.push({ ...base, id: `${base.id}-${kind}`, kind, amount: round2(g.amount), what: g.what });
  };
  for (const e of feed) pushLines({ id: `b${e.id}`, date: e.date || null, at: num(e.at), vendor: e.vendor || 'Expense', status: e.status === 'posted' ? 'Posted' : 'Draft' }, Array.isArray(e.lines) && e.lines.length ? e.lines : [{ amount: e.total, category: e.category, description: e.notes }]);
  for (const e of data.expenses || []) if (e?.projectId === project.id && e.type !== 'income') pushLines({ id: `o${e.id}`, date: e.date || null, at: num(e.createdAt), vendor: e.vendor || e.title || e.description || 'Expense', status: 'Logged here' }, [{ amount: e.amount, category: e.category, description: e.note || null }]);
  for (const r of data.receipts || []) for (const [id, p] of Object.entries(r?.pages || {})) {
    if (!p || p.status === 'void' || seen.has(id)) continue;
    const lines = (p.lines || []).filter((l) => l.projectId === project.id);
    if (lines.length) pushLines({ id: `r${id}`, date: p.date || null, at: num(p.postedAt) || num(r.updatedAt), vendor: p.vendorName || 'Receipt', status: p.status === 'posted' ? 'Posted' : 'Draft' }, lines);
  }
  tx.sort((x, y) => y.at - x.at);
  const kindOf = (k) => { const list = tx.filter((t) => t.kind === k); return { total: round2(list.reduce((a, t) => a + t.amount, 0)), rows: list }; };

  // every person's time on this job, by day and by task
  const owner = (x) => x.ownerUid || project.ownerUid || data.me || '_me';
  const titleOf = new Map(mine.map((t) => [t.id, t.title || 'Task']));
  const who = new Map();
  const person = (uid) => { if (!who.has(uid)) who.set(uid, { uid, ms: 0, travelMs: 0, byDay: new Map(), byTask: new Map() }); return who.get(uid); };
  for (const x of data.sessions || []) {
    if (x?.projectId !== project.id || x.migrated || !x.startedAt) continue;
    const ms = x.endedAt ? num(x.duration) || x.endedAt - x.startedAt : localDateStr(x.startedAt) === today ? Math.max(0, now - x.startedAt) : 0;
    if (!(ms > 0)) continue;
    const p = person(owner(x));
    if (x.type === 'travel') { p.travelMs += ms; continue; }
    p.ms += ms;
    const date = localDateStr(x.startedAt);
    p.byDay.set(date, (p.byDay.get(date) || 0) + ms);
    for (const g of Array.isArray(x.taskSegments) ? x.taskSegments : []) {
      if (!titleOf.has(g.taskId)) continue;
      const seg = Math.max(0, (g.endedAt || x.endedAt || now) - g.startedAt);
      p.byTask.set(g.taskId, (p.byTask.get(g.taskId) || 0) + seg);
    }
  }
  const payRows = new Map((cost?.cfo?.labour?.byPerson || []).map((p) => [p.uid, p]));
  const people = [...who.values()].filter((p) => p.ms > 0 || p.travelMs > 0).map((p) => {
    const r = payRows.get(p.uid);
    return {
      uid: p.uid, name: data.names?.[p.uid] || 'Unknown', ms: p.ms, travelMs: p.travelMs, rate: Math.round(num(r?.rates?.paidOut)) || null, pay: r ? Math.round(num(r.paidOut)) : null,
      days: [...p.byDay.entries()].sort((x, y) => (x[0] < y[0] ? 1 : -1)).map(([date, ms]) => ({ date, ms })),
      tasks: [...p.byTask.entries()].sort((x, y) => y[1] - x[1]).map(([id, ms]) => ({ id, title: titleOf.get(id), ms })),
    };
  }).sort((x, y) => y.ms - x.ms);

  const costs = rows.length || fuelReceipts > 0 || km > 0 || tx.length || people.length || job ? {
    people, materials: kindOf('materials'), fuelTx: kindOf('fuel'), other: kindOf('other'), drives: time?.travel.rows || [], waiting: num(job?.pendingReceipts), waitingTotal: num(job?.pendingReceiptTotal), bookProjectId: project.bookProjectId || job?.projectId || null,
    linked: !!job, bid: bid?.number || null, unpriced: num(bid?.unpriced), rows, posted: round2(rows.reduce((a, r) => a + r.posted, 0)), budget: round2(rows.reduce((a, r) => a + r.budget, 0)),
    fuel: { cost: fuelReceipts > 0 ? round2(fuelReceipts) : round2((km * vehicle.lPer100km * vehicle.fuelPrice) / 100), estimate: !(fuelReceipts > 0), km },
  } : null;

  // ── extras ────────────────────────────────────────────────────────────────────────────────────────────────────
  const planAt = planStartFor(project, data.sessions || [], tasks);
  const ex = contract === 'lump_sum' || !contract ? extrasOf(tasks, project, { pay: data.pay || null, roles: data.roles || [], planAt }) : null;
  const extras = ex?.rows.length ? ex : null;

  // ── money: the contract picks the card ────────────────────────────────────────────────────────────────────────
  const value = contractValueOf(project, job) || 0;
  const billedNet = num(job?.revenue);
  const paid = num(job?.paid); const billed = num(job?.billed);
  const hoursMs = num(cost?.hours?.labourMs) || onTask;   // every hour clocked on the job, the same figure as the header's Labour
  const hours = hoursMs / H;
  let money = null;
  if (contract === 'lump_sum') {
    const L = labourInPrice(project, job, { estMinutes: projectEstMinutes(tasks, project.id), policy: data.policy || null });
    const earned = L?.labour > 0 && quoted ? L.labour * burn.doneShare : null;
    money = {
      kind: 'lump_sum', value, hoursMs, earnedPerHour: earned != null && hours > 0 ? Math.round(earned / hours) : null, soldPerHour: L?.labour > 0 && L.hours > 0 ? Math.round(L.labour / L.hours) : null, soldFrom: L?.from || null,
      donePct: where.pct, billedPct: value > 0 && job ? Math.round((billedNet / value) * 100) : null, paidPct: value > 0 && billed > 0 ? Math.round(((paid / billed) * billedNet / value) * 100) : job ? 0 : null,
      billed, paid, billedNet, earned: earned != null ? round2(earned) : null, labour: L?.labour > 0 ? round2(L.labour) : null, labourHours: L?.hours || null, earnedValue: quoted && value > 0 ? round2(value * burn.doneShare) : null, linked: !!job, aheadOfWork: job && quoted && value > 0 ? round2(billedNet - value * burn.doneShare) : null,
    };
  } else if (contract === 'hourly') {
    const rate = num(project.hourlyRate) || null;
    const earned = num(cost?.cfo?.revenue?.amount) || (rate ? round2((num(cost?.hours?.labourMs) / H) * rate) : 0);
    money = { kind: 'hourly', rate, hoursMs: num(cost?.hours?.labourMs), earned: round2(earned), billed, paid, linked: !!job, toBill: round2(Math.max(0, earned - billedNet)) };
  } else if (contract === 'cost_plus') {
    const costsToDate = num(job?.costToDate);
    money = { kind: 'cost_plus', costs: costsToDate, billed, paid, linked: !!job, unbilled: round2(Math.max(0, costsToDate - billedNet)) };
  } else if (contract === 'not_to_exceed') {
    const rate = num(project.hourlyRate) || 0;
    const used = round2((num(cost?.hours?.labourMs) / H) * rate + num(job?.costToDate));
    money = { kind: 'not_to_exceed', cap: value, used, left: round2(value - used), usedPct: value > 0 ? Math.round((used / value) * 100) : null, billed, paid, linked: !!job, warn: value > 0 && used / value >= 0.75 };
  }

  // ── watch: what could bite, short ─────────────────────────────────────────────────────────────────────────────
  const watch = [];
  if (units?.open.length) watch.push({ key: 'open', card: 'units', tone: 'bad', title: `${units.open.join(', ')} open`, sub: 'Cover before you leave' });
  const late = next.filter((n) => n.late).length ? openTop.filter((t) => t.scheduledDate && t.scheduledDate < today).length : 0;
  if (late) watch.push({ key: 'late', title: `${late} late`, sub: next[0]?.task?.title || '', taskId: next[0]?.task?.id || null, card: 'next' });
  if (quoted && contract && value > 0 && burn.usedShare > burn.doneShare + 0.15 && burn.usedShare > 0.25) watch.push({ key: 'hours', card: 'time', title: 'Hours ahead of work', sub: `${Math.round(burn.usedShare * 100)}% used, ${where.pct}% done` });
  if (num(bid?.unpriced) > 0) watch.push({ key: 'unpriced', card: 'costs', title: `${bid.unpriced} line${bid.unpriced === 1 ? '' : 's'} unpriced`, sub: bid.number || 'On the bid' });
  if (costs?.waiting > 0) watch.push({ key: 'receipts', card: 'costs', title: `${costs.waiting} draft receipt${costs.waiting === 1 ? '' : 's'}`, sub: `$${Math.round(costs.waitingTotal).toLocaleString('en-CA')}` });
  if (money?.kind === 'lump_sum' && !money.linked && value > 0) watch.push({ key: 'link', card: 'money', title: 'No Books job', sub: 'Receipts + payments missing' });
  if (money?.kind === 'lump_sum' && money.linked && money.billedPct != null && money.donePct - money.billedPct >= 15) watch.push({ key: 'bill', card: 'money', title: 'Billing behind', sub: `${money.donePct}% done, ${money.billedPct}% billed` });
  if (money?.kind === 'hourly' && money.toBill >= 500) watch.push({ key: 'tobill', card: 'money', title: 'Time not billed', sub: `$${Math.round(money.toBill).toLocaleString('en-CA')}` });
  if (money?.kind === 'cost_plus' && money.unbilled > 0) watch.push({ key: 'unbilled', card: 'money', title: 'Costs not billed', sub: `$${Math.round(money.unbilled).toLocaleString('en-CA')}` });
  if (money?.warn) watch.push({ key: 'cap', card: 'money', tone: 'bad', title: `${money.usedPct}% of the cap`, sub: `$${Math.round(money.left).toLocaleString('en-CA')} left` });
  const notOk = extras ? extras.rows.filter((r) => r.clientOk === false && !r.billed).length : 0;
  if (notOk) watch.push({ key: 'client', card: 'extras', title: `${notOk} extra${notOk === 1 ? '' : 's'} not OK'd`, sub: 'Ask the client' });

  // ── a backlog (software) has no finish line ───────────────────────────────────────────────────────────────────
  const hasTag = (t, tag) => (t.tags || []).some((x) => String(x).toLowerCase() === tag);
  const backlog = type?.dashboard?.backlog ? (() => {
    const list = top.filter(open);
    const bugs = list.filter((t) => hasTag(t, 'bug')).length; const later = list.filter((t) => hasTag(t, 'later')).length;
    return { open: list.length, done: doneN, bugs, later, other: list.length - bugs - later };
  })() : null;

  // every hourly rate this job has, in one place, each saying what it is. Profit counts only the costs posted so far.
  if (money && (money.kind === 'lump_sum' || money.kind === 'hourly')) {
    const P = cost?.cfo?.perHour || {};
    money.rates = {
      sold: money.kind === 'hourly' ? money.rate : money.soldPerHour,
      earned: money.kind === 'lump_sum' ? money.earnedPerHour : money.rate,
      pay: time?.yourTime?.rate || null,
      profitPaid: P.profitPaidOut != null ? Math.round(P.profitPaidOut) : null,
      profitBefore: P.profitActual != null ? Math.round(P.profitActual) : null,
      costsMissing: !!costs?.rows.some((r) => r.key === 'materials' && r.budget > 0 && !(r.posted > 0)),
    };
  }

  return { contract, where, watch, backlog, next, allOpen, phases, units, plan, time, costs, extras, money, yourTime: num(cost?.cfo?.labour?.paidOut) > 0 ? Math.round(num(cost.cfo.labour.paidOut)) : null };
}
